if (!customElements.get('collection-load-more')) {
  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();

      this.isLoading = false;
      this.abortController = null;
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('[data-load-more-button]');
      this.label = this.querySelector('[data-load-more-label]');
      this.spinner = this.querySelector('[data-load-more-spinner]');
      this.status = this.querySelector('[data-load-more-status]');

      if (!this.button) return;

      this.defaultLabel = this.label?.textContent.trim() || 'Load more';
      this.button.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleClick);
      this.abortController?.abort();
    }

    shouldUseNativeNavigation(event) {
      return (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        this.button.target === '_blank' ||
        this.button.hasAttribute('download')
      );
    }

    async handleClick(event) {
      if (this.shouldUseNativeNavigation(event) || this.isLoading) return;

      event.preventDefault();

      const fallbackUrl = this.button.href;
      const sectionId = this.dataset.sectionId;

      if (!fallbackUrl || !sectionId) {
        window.location.assign(fallbackUrl);
        return;
      }

      this.setLoading(true);
      this.abortController = new AbortController();

      try {
        const requestUrl = new URL(fallbackUrl, window.location.origin);
        requestUrl.searchParams.set('section_id', sectionId);

        const response = await fetch(requestUrl.toString(), {
          signal: this.abortController.signal,
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) {
          throw new Error(`Load more request failed: ${response.status}`);
        }

        const responseText = await response.text();
        const parsedDocument = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

        const currentContainer = this.closest('#ProductGridContainer');
        const currentGrid = currentContainer?.querySelector('#product-grid');
        const incomingGrid = parsedDocument.querySelector('#product-grid');

        if (!currentGrid || !incomingGrid) {
          throw new Error('Product grid was not found in the response.');
        }

        const incomingItems = Array.from(incomingGrid.children).filter(
          (item) => item.matches('li.grid__item')
        );

        if (incomingItems.length === 0) {
          throw new Error('No additional products were returned.');
        }

        const currentItemCount = currentGrid.children.length;
        const fragment = document.createDocumentFragment();

        incomingItems.forEach((item, index) => {
          /*
           * Every image added after the initial page should remain lazy-loaded,
           * including the first two images of every paginated response.
           */
          item.querySelectorAll('img').forEach((image) => {
            image.loading = 'lazy';
            image.removeAttribute('fetchpriority');
          });

          /*
           * Dynamically inserted cards should be immediately visible.
           * This avoids reinitialising animation observers for the entire grid.
           */
          item.classList.remove(
            'scroll-trigger--offscreen',
            'scroll-trigger--cancel'
          );

          if (item.hasAttribute('data-cascade')) {
            item.style.setProperty(
              '--animation-order',
              currentItemCount + index + 1
            );
          }

          fragment.appendChild(item);
        });

        currentGrid.appendChild(fragment);

        const incomingButton = parsedDocument.querySelector(
          '[data-load-more-button]'
        );

        if (incomingButton?.getAttribute('href')) {
          this.button.setAttribute(
            'href',
            incomingButton.getAttribute('href')
          );
        } else {
          this.button.remove();
          this.button = null;
        }

        if (this.status) {
          this.status.textContent =
            `${incomingItems.length} more products loaded.`;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('[Collection load more]', error);

        /*
         * Native pagination remains the fallback if AJAX loading fails.
         */
        window.location.assign(fallbackUrl);
      } finally {
        this.abortController = null;

        if (this.isConnected && this.button) {
          this.setLoading(false);
        }
      }
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;

      if (!this.button) return;

      this.button.setAttribute('aria-busy', String(isLoading));
      this.button.setAttribute(
        'aria-disabled',
        String(isLoading)
      );

      this.button.classList.toggle('is-loading', isLoading);

      if (this.label) {
        this.label.textContent = isLoading
          ? 'Loading...'
          : this.defaultLabel;
      }

      if (this.spinner) {
        this.spinner.classList.toggle('hidden', !isLoading);

        const innerSpinner =
          this.spinner.querySelector('.loading__spinner');

        innerSpinner?.classList.toggle('hidden', !isLoading);
      }
    }
  }

  customElements.define(
    'collection-load-more',
    CollectionLoadMore
  );
}